// SuperTopBar — fixed header for the platform super-admin shell.
//
// Standalone from the tenant TopBar: shows the Tricivo Admin brand on the
// left, the platform-admin identity on the right, and the impersonation
// banner above the bar when an impersonation session is active. No
// company name from the tenant authStore, no module-nav links, no Quick
// Links menu.

import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useSuperAdminStore } from '../../store/superAdminStore';
import { useSuperAuth } from '../../auth/useSuperAuth';
import { Avatar } from '../ui/Avatar/Avatar';
import { Badge } from '../ui/Badge/Badge';
import styles from './SuperTopBar.module.css';

export function SuperTopBar(): JSX.Element {
  const superAdmin = useSuperAdminStore((s) => s.admin);
  const isImpersonating = useSuperAdminStore((s) => s.isImpersonating);
  const impersonatedCompany = useSuperAdminStore((s) => s.impersonatedCompany);
  const stopImpersonation = useSuperAdminStore((s) => s.stopImpersonation);
  const setAuthStoreAnonymous = useAuthStore((s) => s.setAnonymous);
  const { logout: superLogout } = useSuperAuth();
  const navigate = useNavigate();

  const adminName = superAdmin?.name && superAdmin.name.length > 0
    ? superAdmin.name
    : 'Platform Admin';

  async function handleLogout() {
    await superLogout();
    navigate('/super/login', { replace: true });
  }

  function handleStopImpersonation() {
    stopImpersonation();
    setAuthStoreAnonymous();
    navigate('/super/companies');
  }

  return (
    <>
      {isImpersonating && impersonatedCompany ? (
        <div role="alert" className={styles.impersonationBanner}>
          <ShieldAlert size={18} aria-hidden />
          <span className={styles.impersonationText}>
            <strong>Impersonating:</strong> {impersonatedCompany.name}
          </span>
          <button
            type="button"
            className={styles.impersonationStop}
            onClick={handleStopImpersonation}
          >
            Stop Impersonating
          </button>
        </div>
      ) : null}

      <header role="banner" className={styles.bar}>
        <Link to="/super" className={styles.brand} aria-label="Tricivo Admin dashboard">
          <span className={styles.brandMark}>Tricivo</span>
          <span className={styles.brandSuffix}>Admin</span>
        </Link>

        <div className={styles.spacer}>
          <span className={styles.platformLabel} title="Tricivo Platform Admin">
            Tricivo Platform Admin
          </span>
        </div>

        <div className={styles.actions}>
          <div className={styles.user}>
            <Avatar name={adminName} size="sm" />
            <div className={styles.userInfo}>
              <span className={styles.userName}>{adminName}</span>
              <Badge variant="muted">super</Badge>
            </div>
          </div>

          <button type="button" className={styles.logout} onClick={handleLogout}>
            <LogOut size={14} aria-hidden />
            Logout
          </button>
        </div>
      </header>
    </>
  );
}
